import type { StatusItem } from 'pages/crm-tracts-subtracts/types';
import { STATUS_DISPLAY_MAP } from 'pages/crm-tracts-subtracts/utils';
import { formatNumber } from 'utils/number';
import { isBlank } from './fields';
import type {
  BatchRow,
  BatchStatus,
  BulkAssetBatch,
  RowOutcome,
  RowValues,
} from './types';

/** How often the results poll the batch while it runs. */
export const BATCH_POLL_MS = 2000;

/** How often the Bulk uploads list polls while a listed batch runs. */
export const LIST_POLL_MS = 4000;

export const isBatchActive = (status: BatchStatus) =>
  status === 'pending' || status === 'processing';

export const OUTCOME_LABELS: Record<RowOutcome, string> = {
  pending: 'Creating',
  creating: 'Creating',
  success: 'Success',
  success_with_issues: 'Success with issues',
  failed: 'Failed',
  stalled: 'Still running',
  skipped: 'Skipped',
};

/** The outcomes Retry failed assets is for. */
const RETRY_OUTCOMES: RowOutcome[] = [
  'failed',
  'success_with_issues',
  'stalled',
];

export const isRetryCandidate = (row: BatchRow) =>
  RETRY_OUTCOMES.includes(row.outcome);

/**
 * The server's rule: a stalled row can always be retried; a failed or
 * with-issues row only once its process has stopped, since later steps may
 * still be running.
 * @param {BatchRow} row - A batch row
 * @return {boolean} Whether a retry may include it
 */
export const isRetryable = (row: BatchRow) =>
  row.outcome === 'stalled' ||
  ((row.outcome === 'failed' || row.outcome === 'success_with_issues') &&
    !!row.finishedAt);

/**
 * Lots or Grid Exception rows skip the geometry and GIS steps by design, so a
 * Success there still leaves GIS to make the shape.
 * @param {BatchRow} row - A batch row
 * @return {boolean} Whether to show the Needs GIS Shape note
 */
export const needsGisShape = (row: BatchRow) =>
  row.outcome === 'success' &&
  (!isBlank(row.payload.lots) || row.payload.gridException === true);

export const isSkipped = (row: BatchRow) => row.outcome === 'skipped';

/**
 * The invalid rows the batch lists as skipped.
 * @param {BulkAssetBatch} batch - The batch
 * @return {number} How many
 */
export const skippedCount = (batch: BulkAssetBatch) =>
  batch.rows.filter(isSkipped).length;

/**
 * The retry body: every retryable row, with edited values for the ones the
 * user changed. A row whose asset exists is never sent values.
 * @param {BatchRow[]} rows - The batch's rows
 * @param {object} edits - Edited values by row id
 * @return {object[]} The `rows` of POST .../retry
 */
export const retryRequestRows = (
  rows: BatchRow[],
  edits: Record<number, RowValues>
) =>
  rows.filter(isRetryable).map((row) => {
    return edits[row.id] && !row.asset
      ? { id: row.id, values: edits[row.id] }
      : { id: row.id };
  });

/**
 * The step and reason that explain a Failed or Success with issues row.
 * @param {BatchRow} row - A batch row
 * @return {string | null} "Step: reason", or null when there is no issue
 */
export const issueText = (row: BatchRow) => {
  if (row.outcome !== 'failed' && row.outcome !== 'success_with_issues') {
    return null;
  }
  const reason = row.failureReason?.trim();
  if (!row.failureStep) return reason || null;
  return reason ? `${row.failureStep}: ${reason}` : row.failureStep;
};

/**
 * The creation-details list: the server's steps with the app's step labels,
 * each failure reason beside its step, plus the entity link when it failed. A
 * row whose tract Salesforce rejected never got a process, so its failing step
 * is the one line.
 * @param {BatchRow} row - A batch row
 * @return {StatusItem[]} Checklist rows
 */
export const stepItems = (row: BatchRow): StatusItem[] => {
  const items: StatusItem[] = row.steps.map((step, index) => ({
    name: `${index}-${step.humanName}`,
    label: `${step.humanName}:`,
    value: STATUS_DISPLAY_MAP[step.status] ?? step.status,
    status: step.status,
    details: step.failureReason ?? undefined,
  }));
  if (items.length === 0 && row.failureStep) {
    items.push({
      name: 'failure-step',
      label: `${row.failureStep}:`,
      value: STATUS_DISPLAY_MAP.failed,
      status: 'failed',
      details: row.failureReason ?? undefined,
    });
  }
  if (row.entityLinkFailureReason) {
    items.push({
      name: 'entity-link',
      label: 'Create Entity Link:',
      value: STATUS_DISPLAY_MAP.failed,
      status: 'failed',
      details: row.entityLinkFailureReason,
    });
  }
  return items;
};

/**
 * The popover trigger's icon: done, failed, or still going.
 * @param {BatchRow} row - A batch row
 * @return {string} A workflow status the popover knows
 */
export const overallStepStatus = (row: BatchRow) => {
  if (row.outcome === 'success') return 'processed';
  if (row.outcome === 'failed' || row.outcome === 'success_with_issues') {
    return 'failed';
  }
  return 'processing';
};

const assets = (count: number, adjective = '') =>
  `${formatNumber({ value: count, minPrecision: 0 })} ${adjective}asset${
    count === 1 ? '' : 's'
  }`;
const wasWere = (count: number) => (count === 1 ? 'was' : 'were');

/**
 * The results sentence: created (with or without issues), could not create,
 * still running, and skipped.
 * @param {BulkAssetBatch} batch - The batch
 * @return {string} The summary
 */
export const resultsSummary = (batch: BulkAssetBatch) => {
  const created = batch.createdCount + batch.withIssuesCount;
  const stalled = batch.rows.filter((row) => row.outcome === 'stalled').length;
  const skipped = skippedCount(batch);
  return [
    created > 0
      ? `${assets(created)} ${wasWere(created)} created.`
      : 'No assets were created.',
    batch.failedCount > 0 &&
      `${assets(batch.failedCount)} could not be created.`,
    stalled > 0 &&
      `${assets(stalled)} ${stalled === 1 ? 'is' : 'are'} still running.`,
    skipped > 0 &&
      `${assets(skipped, 'invalid ')} ${wasWere(skipped)} skipped.`,
  ]
    .filter(Boolean)
    .join(' ');
};

/**
 * "Creating N assets. M invalid assets will be skipped."
 * @param {number} creating - Rows submitted
 * @param {number} skipped - Invalid rows left out
 * @return {string} The summary
 */
export const creatingSummary = (creating: number, skipped: number) =>
  [
    `Creating ${assets(creating)}.`,
    skipped > 0 && `${assets(skipped, 'invalid ')} will be skipped.`,
  ]
    .filter(Boolean)
    .join(' ');

const tracts = (count: number, adjective = '') =>
  `${formatNumber({ value: count, minPrecision: 0 })} ${adjective}tract${
    count === 1 ? '' : 's'
  }`;

/**
 * The review sentence, in the PRD's words.
 * @param {number} valid - Rows that will be created
 * @param {number} invalid - Rows that will be skipped
 * @return {string} The summary
 */
export const reviewSummary = (valid: number, invalid: number) =>
  [
    `Based on the curated tract list, ${tracts(valid)} will be created.`,
    invalid > 0 &&
      `${tracts(invalid, 'invalid ')} will be skipped and will not be created.`,
  ]
    .filter(Boolean)
    .join(' ');
